import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Award, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Laurel } from "@/components/Laurels";

const BLANK = { festival: "", award: "Official Selection", year: "" };

const Row = ({ row, i, onChange, onRemove }) => (
  <div className="grid grid-cols-[1fr_1fr_80px_auto] gap-2" data-testid={`laurel-row-${i}`}>
    <Input value={row.festival} onChange={(e) => onChange(i, "festival", e.target.value)} placeholder="Festival name" className="bg-ink border-white/10" data-testid={`laurel-festival-${i}`} />
    <Input value={row.award} onChange={(e) => onChange(i, "award", e.target.value)} placeholder="Official Selection, Winner…" className="bg-ink border-white/10" data-testid={`laurel-award-${i}`} />
    <Input value={row.year} onChange={(e) => onChange(i, "year", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Year" className="bg-ink border-white/10" data-testid={`laurel-year-${i}`} />
    <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(i)} aria-label="Remove laurel" data-testid={`laurel-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
  </div>
);

// Owner/admin dialog to manage a film's festival laurels.
export default function LaurelsEditor({ film, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const openWith = (o) => {
    if (o) setRows((film.laurels || []).map((l) => ({ ...l, year: l.year ? String(l.year) : "" })));
    setOpen(o);
  };
  const change = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const remove = (i) => setRows((p) => p.filter((_, j) => j !== i));

  const save = async () => {
    const clean = rows.filter((r) => r.festival.trim());
    if (clean.some((r) => r.festival.trim().length < 2 || r.award.trim().length < 2)) return toast.error("Each laurel needs a festival name and an award");
    setBusy(true);
    try {
      const { data } = await api.put(`/films/${film.id}/laurels`, { laurels: clean.map((r) => ({ festival: r.festival, award: r.award, year: r.year ? Number(r.year) : null })) });
      onSaved(data.laurels);
      toast.success("Laurels saved");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save laurels");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={openWith}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-gold/40 text-gold hover:bg-gold/10" data-testid="manage-laurels-btn">
          <Award className="w-4 h-4" /> {film.laurels?.length ? "Edit festival laurels" : "Add festival laurels"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-white/10 max-w-2xl" data-testid="laurels-dialog">
        <DialogHeader><DialogTitle className="font-display">Festival laurels</DialogTitle></DialogHeader>
        <p className="text-sm text-foreground/60 -mt-2">List festivals that selected or awarded this film. They show as gold laurels on the film page.</p>
        <div className="space-y-2 max-h-[45vh] overflow-y-auto pr-1">
          {rows.map((r, i) => <Row key={i} row={r} i={i} onChange={change} onRemove={remove} />)}
          {rows.length === 0 && <p className="text-xs text-foreground/50">No laurels yet.</p>}
        </div>
        {rows.length < 8 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setRows((p) => [...p, { ...BLANK }])} className="w-fit" data-testid="laurel-add-btn">
            <Plus className="w-4 h-4" /> Add laurel
          </Button>
        )}
        {rows.some((r) => r.festival.trim()) && (
          <div className="flex flex-wrap gap-4 p-4 rounded-lg bg-ink border border-white/5" data-testid="laurels-preview">
            {rows.filter((r) => r.festival.trim()).map((r, i) => <Laurel key={i} laurel={r} index={`preview-${i}`} />)}
          </div>
        )}
        <Button onClick={save} disabled={busy} className="bg-terracotta hover:bg-ember text-white" data-testid="laurels-save-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save laurels
        </Button>
      </DialogContent>
    </Dialog>
  );
}
